// ── Block-level Case When code helpers (pure) ────────────────────────────
// Config: logic_mode "builder"|"code" (absent = builder) and
// code {v:1, lang:"js", src, reviewed}. In code mode the dropdown cases, else
// and extras' values, and per-case code are dormant: kept, never edited here.
// The server treats logic_mode:"code" with a blank src as malformed, so
// "code" is only ever written together with a non-empty source (Apply).

export function isCodeBlock(c) {
  return !!c && c.logic_mode === 'code';
}

export function hasSavedBlockCode(c) {
  return typeof c?.code?.src === 'string' && c.code.src.trim() !== '';
}

// Written by Apply: both keys, in one patch.
export function codeBlockPatch(src) {
  return { logic_mode: 'code', code: { v: 1, lang: 'js', src, reviewed: true } };
}

// Back to the dropdown view: only the switch changes; code and every dormant
// field stay exactly as they were.
export const builderBlockPatch = { logic_mode: 'builder' };

// Choosing "Code": the patch to write now, or null when the choice must stay
// local UI state until Apply (nothing saved to switch to).
export function blockToggleWrite(c) {
  return hasSavedBlockCode(c) ? { logic_mode: 'code' } : null;
}

// First-open Cancel / close without Apply: writes nothing. Returns the patch
// (always null) so the rule is testable; the caller just drops its local flag.
export function cancelFirstOpenWrite() {
  return null;
}

export function needsBlockReview(c) {
  return isCodeBlock(c) && c.code?.reviewed === false;
}

// Declared output names in config order: primary target, then each extra.
export function outputNamesOf(c) {
  const cfg = c || {};
  return [cfg.target ?? 'result', ...(cfg.extra_outputs || []).map(e => e?.target ?? '')];
}

// Per-output problems for the Output columns section: blank or duplicate.
// Returns {ok, errors: [string|null per output], first: string|null}.
// The server's preview wants 1–50 unique names of 1–200 characters.
export function validateOutputNames(names) {
  const list = (names || []).map(n => String(n ?? ''));
  const seen = new Map();
  list.forEach((n, i) => { const k = n.trim(); if (k) seen.set(k, [...(seen.get(k) || []), i]); });
  const errors = list.map((n) => {
    const k = n.trim();
    if (!k) return 'Name this output column.';
    if (k.length > 200) return 'Use 200 characters or fewer.';
    if ((seen.get(k) || []).length > 1) return 'Another output column has this name.';
    return null;
  });
  let first = errors.find(Boolean) || null;
  if (!first && list.length > 50) first = 'At most 50 output columns.';
  return { ok: !first, errors, first };
}

// First `max` lines of the code, plus how many were left out.
export function firstLines(src, max = 8) {
  const lines = String(src ?? '').replace(/\s+$/, '').split('\n');
  return { text: lines.slice(0, max).join('\n'), more: Math.max(0, lines.length - max) };
}

// Canvas summary text.
export function codeBlockSummary(c) {
  const n = 1 + (c?.extra_outputs || []).length;
  return `Code · ${n} output${n === 1 ? '' : 's'}${needsBlockReview(c) ? ' · needs review' : ''}`;
}
